/**
 * Bespoke gallery + fullscreen lightbox for the Autodoc product template.
 * Pairs with product-media-gallery-autodoc.liquid and product-gallery-autodoc.css.
 */

class ProductLightboxAutodoc {
  constructor(root, gallery) {
    this.root = root;
    this.gallery = gallery;
    this.track = root.querySelector('[data-pdp-lightbox-track]');
    this.slides = Array.from(root.querySelectorAll('[data-pdp-lightbox-slide]'));
    this.closeBtn = root.querySelector('[data-pdp-lightbox-close]');
    this.prevBtn = root.querySelector('[data-pdp-lightbox-prev]');
    this.nextBtn = root.querySelector('[data-pdp-lightbox-next]');
    this.counter = root.querySelector('[data-pdp-lightbox-counter]');
    this.hint = root.querySelector('[data-pdp-lightbox-hint]');
    this.activeIndex = 0;
    this.lastFocused = null;
    this.zoomStates = new Map();

    this._bindChrome();
    this._bindKeyboard();
    this._bindObserver();
    this._bindZoomGestures();
  }

  open(index, trigger) {
    this.lastFocused = trigger || document.activeElement;
    this.root.hidden = false;
    document.body.classList.add('pdp-lightbox-open');
    this.jumpTo(index);
    requestAnimationFrame(() => {
      if (this.closeBtn) this.closeBtn.focus({ preventScroll: true });
    });
    if (this.hint) {
      this.hint.classList.remove('is-hidden');
      clearTimeout(this._hintTimer);
      this._hintTimer = setTimeout(() => this.hint.classList.add('is-hidden'), 2600);
    }
  }

  close() {
    this.root.hidden = true;
    document.body.classList.remove('pdp-lightbox-open');
    this.slides.forEach((slide) => this._resetZoom(slide));
    this._unlockTrackScroll();
    if (this.lastFocused && typeof this.lastFocused.focus === 'function') {
      this.lastFocused.focus({ preventScroll: true });
    }
  }

  jumpTo(index) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[clamped];
    if (!slide || !this.track) return;
    this._unlockTrackScroll();
    this.track.scrollTo({ left: slide.offsetLeft, behavior: 'auto' });
    this._setActive(clamped);
  }

  goTo(index, behavior) {
    const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
    const slide = this.slides[clamped];
    if (!slide || !this.track) return;
    this._unlockTrackScroll();
    this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior || 'smooth' });
  }

  _unlockTrackScroll() {
    if (this.track) this.track.style.overflowX = '';
  }

  _setActive(index) {
    if (index !== this.activeIndex) {
      const previousSlide = this.slides[this.activeIndex];
      if (previousSlide) this._resetZoom(previousSlide);
      this._unlockTrackScroll();
    }
    this.activeIndex = index;
    if (this.counter) {
      this.counter.textContent = (index + 1) + ' / ' + this.slides.length;
    }
    if (this.gallery && typeof this.gallery.setActive === 'function') {
      this.gallery.setActive(index);
    }
    if (this.gallery && typeof this.gallery.scrollToIndex === 'function') {
      this.gallery.scrollToIndex(index, 'auto');
    }
  }

  _bindChrome() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.close());
    }
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.goTo(this.activeIndex - 1));
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.goTo(this.activeIndex + 1));
    }
    this.root.addEventListener('click', (event) => {
      if (event.target === this.root) this.close();
    });
  }

  _bindKeyboard() {
    document.addEventListener('keydown', (event) => {
      if (this.root.hidden) return;
      if (event.key === 'Escape') this.close();
      if (event.key === 'ArrowLeft') this.goTo(this.activeIndex - 1);
      if (event.key === 'ArrowRight') this.goTo(this.activeIndex + 1);
    });
  }

  _bindObserver() {
    if (!this.track || this.slides.length === 0) return;
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            const index = Number(entry.target.dataset.index);
            if (index !== this.activeIndex) this._setActive(index);
          }
        });
      },
      { root: this.track, threshold: [0, 0.6, 1] }
    );
    this.slides.forEach((slide) => this.observer.observe(slide));
  }

  _resetZoom(slide) {
    const img = slide.querySelector('.pdp-lightbox__image');
    if (!img) return;
    img.style.transform = '';
    this.zoomStates.set(img, { scale: 1, x: 0, y: 0 });
  }

  _bindZoomGestures() {
    this.slides.forEach((slide) => {
      const area = slide.querySelector('[data-pdp-zoom-area]');
      const img = slide.querySelector('.pdp-lightbox__image');
      if (!area || !img) return;

      this.zoomStates.set(img, { scale: 1, x: 0, y: 0 });

      let pinchStartDist = 0;
      let pinchStartScale = 1;
      let panStart = null;
      let lastTapTime = 0;
      let lastTapPoint = null;

      const applyTransform = () => {
        const state = this.zoomStates.get(img);
        img.style.transform = 'translate(' + state.x + 'px, ' + state.y + 'px) scale(' + state.scale + ')';
      };

      const clampState = (state) => {
        state.scale = Math.max(1, Math.min(state.scale, 4));
        if (state.scale === 1) {
          state.x = 0;
          state.y = 0;
          return state;
        }
        const rect = area.getBoundingClientRect();
        const maxX = (rect.width * (state.scale - 1)) / 2;
        const maxY = (rect.height * (state.scale - 1)) / 2;
        state.x = Math.max(-maxX, Math.min(maxX, state.x));
        state.y = Math.max(-maxY, Math.min(maxY, state.y));
        return state;
      };

      const setZoomedTrackScroll = (locked) => {
        if (this.track) this.track.style.overflowX = locked ? 'hidden' : '';
      };

      const distance = (touches) => {
        const dx = touches[0].clientX - touches[1].clientX;
        const dy = touches[0].clientY - touches[1].clientY;
        return Math.sqrt(dx * dx + dy * dy);
      };

      area.addEventListener(
        'touchstart',
        (event) => {
          const state = this.zoomStates.get(img);
          if (event.touches.length === 2) {
            pinchStartDist = distance(event.touches);
            pinchStartScale = state.scale;
            panStart = null;
          } else if (event.touches.length === 1) {
            const touch = event.touches[0];
            const now = Date.now();
            if (lastTapTime && now - lastTapTime < 320 && lastTapPoint) {
              const moved = Math.hypot(touch.clientX - lastTapPoint.x, touch.clientY - lastTapPoint.y);
              if (moved < 30) {
                if (state.scale > 1) {
                  state.scale = 1;
                  state.x = 0;
                  state.y = 0;
                } else {
                  state.scale = 2.4;
                  const rect = area.getBoundingClientRect();
                  state.x = (rect.width / 2 - (touch.clientX - rect.left)) * (state.scale - 1) / state.scale;
                  state.y = (rect.height / 2 - (touch.clientY - rect.top)) * (state.scale - 1) / state.scale;
                }
                clampState(state);
                applyTransform();
                setZoomedTrackScroll(state.scale > 1);
                lastTapTime = 0;
                return;
              }
            }
            lastTapTime = now;
            lastTapPoint = { x: touch.clientX, y: touch.clientY };

            if (state.scale > 1) {
              panStart = { x: touch.clientX, y: touch.clientY, sx: state.x, sy: state.y };
            }
          }
        },
        { passive: true }
      );

      area.addEventListener(
        'touchmove',
        (event) => {
          const state = this.zoomStates.get(img);
          if (event.touches.length === 2) {
            event.preventDefault();
            const newDist = distance(event.touches);
            if (pinchStartDist > 0) {
              state.scale = (pinchStartScale * newDist) / pinchStartDist;
              clampState(state);
              applyTransform();
              setZoomedTrackScroll(state.scale > 1);
            }
          } else if (event.touches.length === 1 && panStart) {
            event.preventDefault();
            const touch = event.touches[0];
            state.x = panStart.sx + (touch.clientX - panStart.x);
            state.y = panStart.sy + (touch.clientY - panStart.y);
            clampState(state);
            applyTransform();
          }
        },
        { passive: false }
      );

      area.addEventListener('touchend', () => {
        const state = this.zoomStates.get(img);
        panStart = null;
        pinchStartDist = 0;
        if (state.scale <= 1.02) {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
          applyTransform();
          setZoomedTrackScroll(false);
        }
      });

      /* Desktop: double-click to zoom, drag to pan while zoomed */
      let isDragging = false;
      let dragStart = null;

      area.addEventListener('dblclick', (event) => {
        const state = this.zoomStates.get(img);
        if (state.scale > 1) {
          state.scale = 1;
          state.x = 0;
          state.y = 0;
        } else {
          state.scale = 2.4;
          const rect = area.getBoundingClientRect();
          state.x = (rect.width / 2 - (event.clientX - rect.left)) * (state.scale - 1) / state.scale;
          state.y = (rect.height / 2 - (event.clientY - rect.top)) * (state.scale - 1) / state.scale;
        }
        clampState(state);
        applyTransform();
        setZoomedTrackScroll(state.scale > 1);
      });

      area.addEventListener('mousedown', (event) => {
        const state = this.zoomStates.get(img);
        if (state.scale <= 1) return;
        isDragging = true;
        dragStart = { x: event.clientX, y: event.clientY, sx: state.x, sy: state.y };
        event.preventDefault();
      });

      window.addEventListener('mousemove', (event) => {
        if (!isDragging || !dragStart) return;
        const state = this.zoomStates.get(img);
        state.x = dragStart.sx + (event.clientX - dragStart.x);
        state.y = dragStart.sy + (event.clientY - dragStart.y);
        clampState(state);
        applyTransform();
      });

      window.addEventListener('mouseup', () => {
        isDragging = false;
        dragStart = null;
      });
    });
  }
}

if (!customElements.get('product-gallery-autodoc')) {
  class ProductGalleryAutodoc extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-pdp-track]');
      this.slides = Array.from(this.querySelectorAll('[data-pdp-slide]'));
      this.dots = Array.from(this.querySelectorAll('[data-pdp-dot]'));
      this.thumbs = Array.from(this.querySelectorAll('[data-pdp-thumb]'));
      this.prevBtn = this.querySelector('[data-pdp-prev]');
      this.nextBtn = this.querySelector('[data-pdp-next]');
      this.lightbox = this.nextElementSibling && this.nextElementSibling.hasAttribute('data-pdp-lightbox')
        ? this.nextElementSibling
        : null;
      this.activeIndex = 0;

      if (!this.track || this.slides.length === 0) return;

      this._setupObserver();
      this._bindDotsAndThumbs();
      this._bindArrows();
      this._bindZoomTriggers();

      if (this.lightbox) {
        this.lightboxController = new ProductLightboxAutodoc(this.lightbox, this);
      }
    }

    _setupObserver() {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
              this.setActive(Number(entry.target.dataset.index));
            }
          });
        },
        { root: this.track, threshold: [0, 0.6, 1] }
      );
      this.slides.forEach((slide) => this.observer.observe(slide));
    }

    setActive(index) {
      if (index === this.activeIndex) return;
      this.activeIndex = index;
      this.dots.forEach((dot) => {
        const isActive = Number(dot.dataset.index) === index;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });
      this.thumbs.forEach((thumb) => {
        thumb.classList.toggle('is-active', Number(thumb.dataset.index) === index);
      });
    }

    scrollToIndex(index, behavior) {
      const clamped = Math.max(0, Math.min(index, this.slides.length - 1));
      const slide = this.slides[clamped];
      if (!slide) return;
      this.track.scrollTo({ left: slide.offsetLeft, behavior: behavior || 'smooth' });
      this.setActive(clamped);
    }

    _bindDotsAndThumbs() {
      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => this.scrollToIndex(Number(dot.dataset.index)));
      });
      this.thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => this.scrollToIndex(Number(thumb.dataset.index)));
      });
    }

    _bindArrows() {
      if (this.prevBtn) {
        this.prevBtn.addEventListener('click', () => this.scrollToIndex(this.activeIndex - 1));
      }
      if (this.nextBtn) {
        this.nextBtn.addEventListener('click', () => this.scrollToIndex(this.activeIndex + 1));
      }
    }

    _bindZoomTriggers() {
      this.querySelectorAll('[data-pdp-open-lightbox]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          if (this.lightboxController) {
            this.lightboxController.open(Number(trigger.dataset.index), trigger);
          }
        });
      });
    }
  }

  customElements.define('product-gallery-autodoc', ProductGalleryAutodoc);
}
